import {
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { v2, type ValidationRule, type ValidationRuleSet } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Spinner } from '@/components/dashboard/Spinner';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { AGENCY_OPTIONS, groupByTier } from '@/lib/validation-rules-grouping';
import { ApiErrorNotice } from '@/components/shared/ApiErrorNotice';
import { TierSection } from '@/components/admin/validation-rules/TierSection';
import { RuleRow } from '@/components/admin/validation-rules/RuleRow';
import { ENFORCEMENT_LABELS } from '@/lib/validation-rule-enforcement';

// Validation Rules rows 3–5 (item 4) — fixed Severity options (the
// ValidationRule severities), like the fixed Agency options: the filters
// never collapse to "All" when the list is empty or filtered.
const SEVERITY_OPTIONS = ['error', 'warning', 'info'] as const;

// Backend error codes for this page: src/routes/validation-rules.ts only
// emits `AuthRequired` (401) today; the route has no per-id operations,
// so no `NotFound` / `ValidationError` codes are reachable. We keep
// `AuthRequired` mapped to a session-expiry hint and leave room for
// future codes without forcing them in now. Phase E-polish-extract
// hoisted the formatXxxError helper to `@/lib/errors`; the per-page
// map below is passed in as the `codeMap` argument.
const VALIDATION_RULE_ERROR_MESSAGES: Record<string, string> = {
  AuthRequired: 'Your session expired. Please sign in again.',
};

export function ValidationRulesPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('ValidationRules', 'read');

  const [rules, setRules] = useState<ValidationRule[] | null>(null);
  // A failed load holds what was thrown (shown by the shared ApiErrorNotice);
  // the rules then stay unknown (null), never an empty list.
  const [loadError, setLoadError] = useState<{ err: unknown } | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [regionFilter, setRegionFilter] = useState<string>('All');
  const [severityFilter, setSeverityFilter] = useState<string>('All');
  // phase-validation-rules-screen — free-text search (code + description)
  // and the agency-vs-internal origin filter. Both COMBINE with the existing
  // dropdowns (logical AND), never replace them.
  const [search, setSearch] = useState<string>('');
  const [originFilter, setOriginFilter] = useState<string>('All');
  // Validation Rules rows 1–2 (item 9) — filter on what the code enforces.
  const [enforcementFilter, setEnforcementFilter] = useState<string>('All');
  // Item 12 — which rule set this is, and the review export.
  const [ruleSet, setRuleSet] = useState<ValidationRuleSet | null>(null);
  const [ruleSetError, setRuleSetError] = useState(false);
  const [exportState, setExportState] = useState<{ kind: 'idle' | 'busy' | 'done' | 'error'; text?: string }>({ kind: 'idle' });

  useEffect(() => {
    if (!canRead) return;
    setRules(null);
    setLoadError(null);
    v2.validationRules
      .list()
      .then(setRules)
      .catch((err: unknown) => {
        // Validation Rules rows 1–2 (item 11) / rows 3–5 (item 3) — a failed
        // load is an ERROR with Retry, never "no rules loaded … the migration
        // may not have run yet": the rules stay unknown (null), not an empty list.
        setLoadError({ err });
      });
    setRuleSet(null);
    setRuleSetError(false);
    v2.validationRules
      .ruleSet()
      .then(setRuleSet)
      .catch(() => setRuleSetError(true));
  }, [canRead, loadKey]);

  const handleExport = async (): Promise<void> => {
    setExportState({ kind: 'busy' });
    try {
      const fileName = await v2.validationRules.exportCsv();
      setExportState({ kind: 'done', text: `Downloaded ${fileName}.` });
    } catch (err) {
      setExportState({ kind: 'error', text: formatApiError(err, 'The export failed.', VALIDATION_RULE_ERROR_MESSAGES) });
    }
  };

  // Validation Rules rows 3–5 (item 4) — the Agency options are the FIXED
  // agency list (AGENCY_OPTIONS), no longer built from the loaded rows, so the
  // filter keeps its options when the list is empty. The filter reads the
  // populated `Agencies` field (phase-fix-validation-rules-region-column):
  // comma-separated values, and All (global/ICH) rules stay visible under
  // every agency selection (V1 parity).

  // Loud on missing agency data — a blank renders 'Unknown' in the table,
  // and we surface it in the console so the gap can't hide.
  useEffect(() => {
    const blanks = (rules ?? []).filter((r) => (r.Agencies ?? '').trim() === '');
    if (blanks.length > 0) {
      console.warn(
        `[ValidationRulesPage] ${blanks.length} rule(s) have no Agencies value and render as 'Unknown':`,
        blanks.map((r) => r.RuleId),
      );
    }
  }, [rules]);

  const filtered = useMemo(() => {
    if (!rules) return [];
    const needle = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (regionFilter !== 'All') {
        const agencies = (r.Agencies ?? '').trim();
        // Global ('All') rules apply to every agency, so they stay visible
        // under any selection (V1 behavior).
        const tokens = agencies.split(',').map((a) => a.trim());
        if (agencies !== 'All' && !tokens.includes(regionFilter)) return false;
      }
      if (severityFilter !== 'All' && r.Severity !== severityFilter) return false;
      // Origin filter — 'agency' vs 'internal' (rows from a pre-migration
      // backend lack Origin and are treated as agency, matching the display).
      if (originFilter !== 'All') {
        const origin = r.Origin === 'internal' ? 'internal' : 'agency';
        if (origin !== originFilter) return false;
      }
      if (enforcementFilter !== 'All' && (r.Enforcement ?? 'unknown') !== enforcementFilter) return false;
      // Free-text search across BOTH the rule code and the description —
      // "a validation run cites a rule code; typing it must land on it".
      if (needle !== '') {
        const code = r.RuleId.toLowerCase();
        const desc = (r.Description ?? '').toLowerCase();
        if (!code.includes(needle) && !desc.includes(needle)) return false;
      }
      return true;
    });
  }, [rules, regionFilter, severityFilter, originFilter, enforcementFilter, search]);

  // Phase 5.X-validation-rules-tier-grouping — restore V1's tier-grouped
  // display. After the existing filter logic runs we bucket the filtered
  // list into canonical-order tier groups and render one collapsible
  // <TierSection> per group. Pagination was dropped — per-tier collapse
  // gives operators an equivalent way to navigate a ~50-rule list, and
  // V1 didn't paginate this page either.
  //
  // Validation Rules rows 3–5 (item 1) — every tier in the loaded catalogue
  // keeps its header whatever the filters leave in it ("0", and "No rules in
  // this tier match" when expanded), in the fixed TIER_ORDER.
  const catalogueTiers = useMemo(() => Array.from(new Set((rules ?? []).map((r) => r.Tier))), [rules]);
  const tierGroups = useMemo(() => groupByTier(filtered, catalogueTiers), [filtered, catalogueTiers]);

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead) return <PermissionDenied />;

  return (
    <section>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Validation Rules</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            Rules used during eCTD package validation. Read-only — managed in code and seeded by
            migrations.
          </p>
          {/* Item 12 — which rule set this is (also recorded on every compiled package). */}
          <p data-testid="rules-ruleset" style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            {ruleSet ? (
              <>
                Rule set <code data-testid="rules-ruleset-version">{ruleSet.version}</code> · {ruleSet.ruleCount} rules · last
                changed {ruleSet.lastChangedAt ? new Date(ruleSet.lastChangedAt).toLocaleString() : 'unknown'}
              </>
            ) : ruleSetError ? (
              'Rule-set version unavailable.'
            ) : (
              'Loading rule-set version…'
            )}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <button
            type="button"
            data-testid="rules-export-btn"
            onClick={() => void handleExport()}
            disabled={exportState.kind === 'busy'}
            title="One CSV row per rule — RuleId, tier, description, source, agencies, severity, origin, enforcement, implementation file, notes — with the rule-set version, for the domain reviewer."
            style={{
              padding: '6px 12px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: 13,
              cursor: exportState.kind === 'busy' ? 'wait' : 'pointer',
            }}
          >
            {exportState.kind === 'busy' ? 'Exporting…' : 'Export for review (CSV)'}
          </button>
          {(exportState.kind === 'done' || exportState.kind === 'error') && (
            <div
              data-testid="rules-export-status"
              role={exportState.kind === 'error' ? 'alert' : 'status'}
              style={{ marginTop: 4, fontSize: 12, color: exportState.kind === 'error' ? 'var(--red)' : 'var(--text-muted)' }}
            >
              {exportState.text}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rule code or description…"
          aria-label="Search rules"
          data-testid="rules-search"
          style={{
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            minWidth: 240,
          }}
        />
        <label style={{ fontSize: 12 }}>
          Agency
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            aria-label="Agency filter"
            style={{
              marginLeft: 8,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <option value="All">All</option>
            {AGENCY_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Severity
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            aria-label="Severity filter"
            style={{
              marginLeft: 8,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <option value="All">All</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Origin
          <select
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            aria-label="Origin filter"
            data-testid="rules-origin-filter"
            style={{
              marginLeft: 8,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <option value="All">All</option>
            <option value="agency">Agency requirements</option>
            <option value="internal">AutoeCTD checks</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Enforcement
          <select
            value={enforcementFilter}
            onChange={(e) => setEnforcementFilter(e.target.value)}
            aria-label="Enforcement filter"
            data-testid="rules-enforcement-filter"
            style={{
              marginLeft: 8,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <option value="All">All</option>
            <option value="enforced">{ENFORCEMENT_LABELS['enforced']}</option>
            <option value="advisory">{ENFORCEMENT_LABELS['advisory']}</option>
            <option value="not-yet-enforced">{ENFORCEMENT_LABELS['not-yet-enforced']}</option>
          </select>
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }} data-testid="rules-count">
          {filtered.length} of {rules?.length ?? 0} rule{(rules?.length ?? 0) === 1 ? '' : 's'}
        </span>
      </div>

      {rules === null && !loadError && <Spinner caption="Loading validation rules…" />}
      {/* Item 11 (rows 1–2) / item 3 (rows 3–5) — a failed load is the shared
          error notice ("Could not load validation rules", the message, the
          correlation id when the server sent one) with Retry. The rules are
          unknown, so neither the tiers nor the "no rules loaded" empty state
          render beside it. */}
      {loadError && (
        <div data-testid="validation-rules-error">
          <ApiErrorNotice
            heading="Could not load validation rules"
            error={loadError.err}
            fallback="The validation rules could not be loaded."
            onRetry={() => setLoadKey((k) => k + 1)}
          />
        </div>
      )}

      {rules !== null && filtered.length === 0 && (
        <div
          data-testid="validation-rules-empty-state"
          style={{
            padding: '40px 16px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 13,
            border: '1px solid var(--border)',
            marginBottom: 16,
            borderRadius: 6,
            background: 'var(--surface)',
          }}
        >
          {rules.length === 0 ? (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">
                ⚖️
              </div>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--text)',
                  marginBottom: 4,
                }}
              >
                No validation rules loaded
              </div>
              <div>
                Validation rules are seeded by Phase 2.x migrations. If this
                list is empty in production, the migration may not have run
                yet.
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--text)',
                  marginBottom: 4,
                }}
              >
                No validation rules match the current filters
              </div>
              <div>
                Showing 0 of {rules.length} rule{rules.length === 1 ? '' : 's'}.
                Clear the search or reset the Agency / Severity / Origin / Enforcement
                filters to see all rules.
              </div>
            </>
          )}
        </div>
      )}

      {/* Phase 5.X-validation-rules-tier-grouping — one collapsible
          section per tier (each its own <table> with its own headers, so
          Phase 5.13d's "headers always visible" invariant holds per tier).
          Validation Rules rows 3–5 (item 1): whenever the catalogue has
          rules, EVERY catalogue tier renders — a tier the filters empty
          shows "0" and says no rules match. Two page-level states
          preserved from Phase 5.13d, above the tiers:
            - rules.length === 0           → the designed empty state
            - rules.length > 0 but filtered → "no rules match the filters"
       */}
      {rules !== null && rules.length > 0 && (
        <div data-testid="validation-rules-groups">
          {tierGroups.map((group) => (
            <TierSection
              key={group.tier}
              tier={group.tier}
              rules={group.rules}
              renderRow={(rule) => <RuleRow key={rule.Id} rule={rule} />}
            />
          ))}
        </div>
      )}
    </section>
  );
}
